import React from 'react';
import { DrawerFitted } from 'app/components/Common/Drawer/Drawer';
import { Box, IconButton, CircularProgress, Card, CardHeader, Grid, CardContent, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { usePromiseTracker } from 'react-promise-tracker';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { theme } from 'styles/global-styles';
import { useDecision } from '@optimizely/react-sdk';
import { ff_credentialing_csp_web } from 'app/constants/FeatureFlags/Placement/Keys';
import ComplianceStatusProgressNotes from './ComplianceStatusProgressNotes';
import ComplianceStatusProgressdNoteInput from './ComplianceStatusProgressdNoteInput';
import { selectComplianceStatusProgressNotes } from 'store/redux-store/placement-details/selectors';
import ComplianceStatusProgressHeader from './ComplianceStatusProgressHeader';
import ComplianceStatusProgressSearchBox from './ComplianceStatusProgressSearchBox';
import { selectUser } from 'oidc/user.selectors';

const useStyles = makeStyles()({
  title: {
    color: theme.palette.system.black,
    fontSize: '16px',
    fontWeight: 450,
    padding: '0px',
  },
  closeIcon: {
    padding: 0,
  },
  fullScreenIcon: {
    padding: '0px 12px 0px 0px',
  },
  MuiCircularProgressRoot: {
    left: '45%',
    position: 'absolute',
    top: '50vh',
  },
  card: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    padding: '4px 12px',
    minHeight: '47px',
  },
  cardContent: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    backgroundColor: theme.palette.system.lightGray,
  },
  complianceNotes: {
    fontSize: '12px',
    fontWeight: 400,
  },
});

const ComplianceStatusProgressDrawer = React.memo(function ComplianceStatusProgressDrawer() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userInfo } = useSelector(selectUser);
  const { openDrawer, fullScreen, notes, notesList,
    expanded, complianceStateProgressNotesHeader } = useSelector(
      selectComplianceStatusProgressNotes,
    );
  const { promiseInProgress: isCallingComplianceStatusAPI } = usePromiseTracker({
    area: 'placement-compliance-status-notes',
    delay: 0,
  });

  const [credentialing_csp_web] = useDecision(ff_credentialing_csp_web);

  const handleClose = (isDirty: boolean) => {
    if (isDirty) {
      dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerNoteCancel(true));
    } else {
      dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerState(false));
      dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerFullScreenState(false));
    }
  };

  return (
    <>
      <DrawerFitted
        height="100vh"
        open={openDrawer}
        onDrawerClose={handleClose}
        width={450}
        top={0}
        overflow={'hidden'}
        fullScreen={fullScreen}
        backgroundColor={theme.palette.system.lightGray}
        shadow
      >
        {isCallingComplianceStatusAPI ? (
          <CircularProgress className={classes.MuiCircularProgressRoot} />
        ) : (
          <Box>
            <Card variant="outlined" className={classes.card}>
              <CardHeader
                title={
                  <Grid container flexDirection="row" justifyContent="space-between" alignItems="center">
                    <Grid item className={classes.title}>
                      {t('placement.profile.complianceStatusProgress.sectionTitle')}
                    </Grid>
                    <Grid item>
                      <IconButton
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        classes={{ root: classes.fullScreenIcon }}
                        onClick={() => {
                          dispatch(
                            placementDetailsAction.toggleComplianceStatusNotesDrawerFullScreenState(!fullScreen),
                          );
                        }}
                      >
                        {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                      </IconButton>
                      <IconButton
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        classes={{ root: classes.closeIcon }}
                        onClick={() => handleClose(expanded)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                }
                className={classes.cardHeader}
              />
              {credentialing_csp_web?.enabled && <ComplianceStatusProgressHeader
                complianceStateProgressNotesHeader={complianceStateProgressNotesHeader}
                fullScreen={fullScreen}
              />
              }
              {credentialing_csp_web?.enabled && <ComplianceStatusProgressSearchBox
                fullScreen={fullScreen}
              />
              }
              <CardContent className={classes.cardContent}>
                {credentialing_csp_web?.enabled ? (
                  <ComplianceStatusProgressNotes
                    cspNotes={notesList}
                    fullScreen={fullScreen}
                    userInfo={userInfo}
                  />
                ) : (
                  <Typography className={classes.complianceNotes}>{notes ?? ''}</Typography>
                )}
              </CardContent>
              {credentialing_csp_web?.enabled && <ComplianceStatusProgressdNoteInput />}
            </Card>
          </Box>
        )}
      </DrawerFitted>
    </>
  );
});

export default ComplianceStatusProgressDrawer;
